import { createHash } from "node:crypto";

import { buildOrgxApiUrl, resolveOrgxAuth, type ResolveOrgxAuthOptions } from "./auth.js";
import { isRecord } from "./fs.js";
import { fetchWithRetry } from "./network.js";

export type OperatingMapMode = "bounded_sync" | "deep_search" | "reconcile";

export interface OperatingMapSourceHealth {
  sourceKind: string;
  status: string;
  cursor: string | null;
  recordsObserved: number;
  limitations: string[];
}

export interface OperatingMapProcessCard {
  processCandidateRef: { id: string; workspaceId: string };
  displayName: string;
  confidence: number;
  nextConfirmationQuestion: string | null;
  candidateTrigger: Record<string, unknown>;
  handoffDelays: Array<{ from: string; to: string; delayMinutes: number | null }>;
  riskFlags: string[];
}

export interface OperatingMapDiscoveryResult {
  run: {
    id: string;
    mode: OperatingMapMode;
    status: string;
    query: string | null;
    observationCount: number;
    candidateProcessCardCount: number;
    citationCount: number;
    sourceHealth: OperatingMapSourceHealth[];
    limitations: string[];
  };
  observations: Array<Record<string, unknown>>;
  processCards: OperatingMapProcessCard[];
  confirmedProcessRefs: Array<{ id: string; workspaceId: string }>;
}

export interface OperatingMapDiscoveryResponse {
  result: OperatingMapDiscoveryResult;
  duplicate: boolean;
}

export interface OperatingMapClientOptions extends ResolveOrgxAuthOptions {}

function parseResponseBody(text: string): unknown {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function formatHttpError(status: number, body: unknown): string {
  if (typeof body === "string" && body.trim()) return `HTTP ${status}: ${body}`;
  if (isRecord(body) && isRecord(body.error)) {
    const code = typeof body.error.code === "string" ? body.error.code : "api_error";
    const message = typeof body.error.message === "string" ? body.error.message : `HTTP ${status}`;
    return `HTTP ${status} ${code}: ${message}`;
  }
  return `HTTP ${status}`;
}

function extractData(payload: unknown): unknown {
  return isRecord(payload) && "data" in payload ? payload.data : payload;
}

function parseDiscoveryResult(payload: unknown): OperatingMapDiscoveryResult {
  const data = extractData(payload);
  if (!isRecord(data) || !isRecord(data.run) || !Array.isArray(data.processCards)) {
    throw new Error("OrgX returned an incomplete operating-map discovery payload.");
  }
  const run = data.run;
  if (typeof run.id !== "string" || typeof run.mode !== "string" || typeof run.status !== "string") {
    throw new Error("OrgX returned an invalid operating-map discovery run.");
  }
  return {
    run: {
      id: run.id,
      mode: run.mode as OperatingMapMode,
      status: run.status,
      query: typeof run.query === "string" ? run.query : null,
      observationCount: typeof run.observationCount === "number" ? run.observationCount : 0,
      candidateProcessCardCount:
        typeof run.candidateProcessCardCount === "number" ? run.candidateProcessCardCount : data.processCards.length,
      citationCount: typeof run.citationCount === "number" ? run.citationCount : 0,
      sourceHealth: Array.isArray(run.sourceHealth) ? (run.sourceHealth as OperatingMapSourceHealth[]) : [],
      limitations: Array.isArray(run.limitations)
        ? run.limitations.filter((item): item is string => typeof item === "string")
        : [],
    },
    observations: Array.isArray(data.observations)
      ? data.observations.filter(isRecord)
      : [],
    processCards: data.processCards.filter(isRecord).map((card) => {
      const ref = isRecord(card.processCandidateRef) ? card.processCandidateRef : {};
      return {
        processCandidateRef: {
          id: typeof ref.id === "string" ? ref.id : "",
          workspaceId: typeof ref.workspaceId === "string" ? ref.workspaceId : "",
        },
        displayName: typeof card.displayName === "string" ? card.displayName : "Unnamed workflow",
        confidence: typeof card.confidence === "number" ? card.confidence : 0,
        nextConfirmationQuestion:
          typeof card.nextConfirmationQuestion === "string" ? card.nextConfirmationQuestion : null,
        candidateTrigger: isRecord(card.candidateTrigger) ? card.candidateTrigger : {},
        handoffDelays: Array.isArray(card.handoffDelays)
          ? card.handoffDelays.filter(isRecord).map((handoff) => ({
              from: typeof handoff.from === "string" ? handoff.from : "unknown",
              to: typeof handoff.to === "string" ? handoff.to : "unknown",
              delayMinutes: typeof handoff.delayMinutes === "number" ? handoff.delayMinutes : null,
            }))
          : [],
        riskFlags: Array.isArray(card.riskFlags)
          ? card.riskFlags.filter((item): item is string => typeof item === "string")
          : [],
      };
    }),
    confirmedProcessRefs: Array.isArray(data.confirmedProcessRefs)
      ? data.confirmedProcessRefs.filter(isRecord).flatMap((ref) =>
          typeof ref.id === "string" && typeof ref.workspaceId === "string"
            ? [{ id: ref.id, workspaceId: ref.workspaceId }]
            : [],
        )
      : [],
  };
}

async function requireOrgxAuth(options: OperatingMapClientOptions = {}) {
  const auth = await resolveOrgxAuth(options);
  if (!auth) {
    throw new Error("No OrgX API key configured. Run `wizard auth login` or set ORGX_API_KEY first.");
  }
  return auth;
}

export function defaultOperatingMapIdempotencyKey(input: {
  workspaceId: string;
  mode: OperatingMapMode;
  query: string | null;
  sourceKinds: string[];
}): string {
  const digest = createHash("sha256")
    .update(JSON.stringify(input))
    .digest("hex")
    .slice(0, 32);
  return `wizard:operating-map:${input.workspaceId}:${digest}`;
}

export async function startOperatingMapDiscovery(
  input: {
    workspaceId: string;
    mode?: OperatingMapMode;
    query?: string | null;
    sourceKinds?: string[];
    idempotencyKey: string;
  },
  options: OperatingMapClientOptions = {},
): Promise<OperatingMapDiscoveryResponse> {
  const auth = await requireOrgxAuth(options);
  const response = await fetchWithRetry(buildOrgxApiUrl("/v1/discovery-runs", auth.baseUrl), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${auth.apiKey}`,
      "Content-Type": "application/json",
      "Idempotency-Key": input.idempotencyKey,
    },
    body: JSON.stringify({
      workspace_id: input.workspaceId,
      mode: input.mode ?? "bounded_sync",
      query: input.query ?? null,
      source_kinds: input.sourceKinds ?? [],
    }),
  });
  const body = parseResponseBody(await response.text());
  if (!response.ok) {
    throw new Error(`Unable to start the operating-map discovery run. ${formatHttpError(response.status, body)}`);
  }
  const result = parseDiscoveryResult(body);
  const meta = isRecord(body) && isRecord(body.meta) ? body.meta : {};
  return { result, duplicate: meta.duplicate === true };
}

export async function proposeOperatingProcessFromMap(
  input: {
    workspaceId: string;
    discoveryRunId: string;
    processCandidateId: string;
    idempotencyKey: string;
  },
  options: OperatingMapClientOptions = {},
): Promise<unknown> {
  const auth = await requireOrgxAuth(options);
  const response = await fetchWithRetry(
    buildOrgxApiUrl(`/v1/discovery-runs/${encodeURIComponent(input.discoveryRunId)}/propose`, auth.baseUrl),
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${auth.apiKey}`,
        "Content-Type": "application/json",
        "Idempotency-Key": input.idempotencyKey,
      },
      body: JSON.stringify({
        workspace_id: input.workspaceId,
        process_candidate_id: input.processCandidateId,
      }),
    },
  );
  const body = parseResponseBody(await response.text());
  if (!response.ok) {
    throw new Error(`Unable to propose the OperatingProcess. ${formatHttpError(response.status, body)}`);
  }
  return extractData(body);
}
